import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { vendorsApi } from '../../network/apis/vendors';
import { productsApi } from '../../network/apis/products';
import Button from '../../components/elements/button';
import { Search, Add, Edit, Trash, Eye, Filter } from '../../assets/icons';
import '../../assets/css/vendor/products.css';

const VendorProducts = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        category: 'all',
        status: 'all',
        stock: 'all'
    });
    const [showFilters, setShowFilters] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [stockModal, setStockModal] = useState({ show: false, product: null, quantity: 0 });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            const response = await vendorsApi.getProducts();
            const data = response.data?.results || response.results || [];
            setProducts(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch products:', err);
            setError('Failed to load products. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await productsApi.getCategories();
            const data = response.data?.results || response.data || response.results || response || [];
            setCategories(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch categories:', err);
        }
    }, []);

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, [fetchProducts, fetchCategories]);

    // Filter products based on search and filters
    const filteredProducts = products.filter(product => {
        const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            product.sku?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesCategory = filters.category === 'all' || 
                               product.category?.id?.toString() === filters.category ||
                               product.category_id?.toString() === filters.category;
        
        const matchesStatus = filters.status === 'all' ||
                             (filters.status === 'active' && product.is_active) ||
                             (filters.status === 'inactive' && !product.is_active);
        
        const matchesStock = filters.stock === 'all' ||
                            (filters.stock === 'in-stock' && product.stock_status === 'IN_STOCK') ||
                            (filters.stock === 'low-stock' && product.stock_status === 'LOW_STOCK') ||
                            (filters.stock === 'out-of-stock' && product.stock_status === 'OUT_OF_STOCK');
        
        return matchesSearch && matchesCategory && matchesStatus && matchesStock;
    });

    const handleToggleActive = async (productId) => {
        try {
            await vendorsApi.toggleProductActive(productId);
            setProducts(prev => prev.map(p => 
                p.id === productId ? { ...p, is_active: !p.is_active } : p
            ));
            setSuccess('Product status updated');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to update product status');
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleDeleteProduct = async (productId) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;
        
        try {
            await vendorsApi.deleteProduct(productId);
            setProducts(prev => prev.filter(p => p.id !== productId));
            setSuccess('Product deleted successfully');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to delete product');
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleUpdateStock = async () => {
        if (!stockModal.product) return;
        
        try {
            await vendorsApi.updateProductStock(stockModal.product.id, stockModal.quantity);
            setProducts(prev => prev.map(p => 
                p.id === stockModal.product.id 
                    ? { 
                        ...p, 
                        stock_quantity: stockModal.quantity,
                        stock_status: stockModal.quantity === 0 ? 'OUT_OF_STOCK' : 
                                     stockModal.quantity < (p.min_stock_level || 10) ? 'LOW_STOCK' : 'IN_STOCK'
                      } 
                    : p
            ));
            setStockModal({ show: false, product: null, quantity: 0 });
            setSuccess('Stock updated successfully');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to update stock');
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleSelectProduct = (productId) => {
        setSelectedProducts(prev => 
            prev.includes(productId) 
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    const handleSelectAll = () => {
        if (selectedProducts.length === filteredProducts.length) {
            setSelectedProducts([]);
        } else {
            setSelectedProducts(filteredProducts.map(p => p.id));
        }
    };

    const getStockStatusClass = (status) => {
        switch (status) {
            case 'IN_STOCK': return 'in-stock';
            case 'LOW_STOCK': return 'low-stock';
            case 'OUT_OF_STOCK': return 'out-of-stock';
            default: return '';
        }
    };

    const getStockStatusLabel = (status) => {
        switch (status) {
            case 'IN_STOCK': return 'In Stock';
            case 'LOW_STOCK': return 'Low Stock';
            case 'OUT_OF_STOCK': return 'Out of Stock';
            default: return status;
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: 'NGN',
            minimumFractionDigits: 0
        }).format(price || 0);
    };

    // Helper to flatten categories with subcategories for dropdown
    const getFlattenedCategories = () => {
        const flattened = [];
        categories.forEach(category => {
            flattened.push({ id: category.id, name: category.name, isParent: true });
            if (category.subcategories && category.subcategories.length > 0) {
                category.subcategories.forEach(sub => {
                    flattened.push({ id: sub.id, name: `  ${sub.name}`, isParent: false, parentId: category.id });
                });
            }
        });
        return flattened;
    };

    if (loading) {
        return (
            <div className="vendor-products">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading products...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="vendor-products">
            {/* Alert Messages */}
            {error && (
                <div className="alert alert-error">
                    <span>{error}</span>
                    <button onClick={() => setError('')}>&times;</button>
                </div>
            )}
            {success && (
                <div className="alert alert-success">
                    <span>{success}</span>
                    <button onClick={() => setSuccess('')}>&times;</button>
                </div>
            )}

            {/* Page Header */}
            <div className="page-header">
                <div className="header-content">
                    <h1>Products</h1>
                    <p className="subtitle">{filteredProducts.length} of {products.length} products</p>
                </div>
                <div className="header-actions">
                    <Link to="/vendor/products/new">
                        <Button
                            icon={<img src={Add} alt="" />}
                            text="Add Product"
                            variant="primary"
                        />
                    </Link>
                </div>
            </div>

            {/* Search & Filters Bar */}
            <div className="toolbar">
                <div className="search-container">
                    <img src={Search} alt="" className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search products by name or SKU..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="clear-search" onClick={() => setSearchTerm('')}>&times;</button>
                    )}
                </div>
                
                <button 
                    className={`filter-toggle ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <img src={Filter} alt="" />
                    Filters
                    {(filters.category !== 'all' || filters.status !== 'all' || filters.stock !== 'all') && (
                        <span className="filter-badge">
                            {[filters.category, filters.status, filters.stock].filter(f => f !== 'all').length}
                        </span>
                    )}
                </button>
            </div>

            {/* Collapsible Filters */}
            {showFilters && (
                <div className="filters-panel">
                    <div className="filter-group">
                        <label>Category</label>
                        <select
                            value={filters.category}
                            onChange={(e) => setFilters({...filters, category: e.target.value})}
                        >
                            <option value="all">All Categories</option>
                            {getFlattenedCategories().map(cat => (
                                <option 
                                    key={cat.id} 
                                    value={cat.id.toString()}
                                    style={{ fontWeight: cat.isParent ? '600' : '400' }}
                                >
                                    {cat.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Status</label>
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters({...filters, status: e.target.value})}
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Stock Level</label>
                        <select
                            value={filters.stock}
                            onChange={(e) => setFilters({...filters, stock: e.target.value})}
                        >
                            <option value="all">All Stock Levels</option>
                            <option value="in-stock">In Stock</option>
                            <option value="low-stock">Low Stock</option>
                            <option value="out-of-stock">Out of Stock</option>
                        </select>
                    </div>

                    <button 
                        className="clear-filters"
                        onClick={() => setFilters({ category: 'all', status: 'all', stock: 'all' })}
                    >
                        Clear All
                    </button>
                </div>
            )}

            {/* Products Table */}
            {filteredProducts.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">📦</div>
                    <h3>No products found</h3>
                    <p>
                        {products.length === 0 
                            ? "You haven't added any products yet. Start by adding your first product."
                            : "No products match your current filters. Try adjusting your search or filters."}
                    </p>
                    {products.length === 0 && (
                        <Link to="/vendor/products/new">
                            <Button text="Add Your First Product" variant="primary" />
                        </Link>
                    )}
                </div>
            ) : (
                <div className="products-table-container">
                    <table className="products-table">
                        <thead>
                            <tr>
                                <th className="checkbox-col">
                                    <input 
                                        type="checkbox" 
                                        checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                <th className="product-col">Product</th>
                                <th>Category</th>
                                <th>Price</th>
                                <th>Stock</th>
                                <th>Status</th>
                                <th className="actions-col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProducts.map(product => (
                                <tr key={product.id} className={selectedProducts.includes(product.id) ? 'selected' : ''}>
                                    <td className="checkbox-col">
                                        <input 
                                            type="checkbox" 
                                            checked={selectedProducts.includes(product.id)}
                                            onChange={() => handleSelectProduct(product.id)}
                                        />
                                    </td>
                                    <td className="product-col">
                                        <div className="product-info">
                                            <div className="product-image">
                                                {product.primary_image || product.images?.[0]?.image ? (
                                                    <img 
                                                        src={product.primary_image || product.images[0].image} 
                                                        alt={product.name} 
                                                    />
                                                ) : (
                                                    <div className="no-image">📷</div>
                                                )}
                                            </div>
                                            <div className="product-details">
                                                <h4>{product.name}</h4>
                                                <span className="sku">SKU: {product.sku || 'N/A'}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="category-tag">
                                            {product.category?.name || 'Uncategorized'}
                                        </span>
                                    </td>
                                    <td className="price">{formatPrice(product.price)}</td>
                                    <td>
                                        <div className="stock-info">
                                            <span className={`stock-badge ${getStockStatusClass(product.stock_status)}`}>
                                                {getStockStatusLabel(product.stock_status)}
                                            </span>
                                            <span className="stock-qty">{product.stock_quantity || 0} units</span>
                                        </div>
                                    </td>
                                    <td>
                                        <button 
                                            className={`status-toggle ${product.is_active ? 'active' : 'inactive'}`}
                                            onClick={() => handleToggleActive(product.id)}
                                            title={product.is_active ? 'Click to deactivate' : 'Click to activate'}
                                        >
                                            <span className="toggle-dot"></span>
                                            <span className="toggle-label">{product.is_active ? 'Active' : 'Inactive'}</span>
                                        </button>
                                    </td>
                                    <td className="actions-col">
                                        <div className="action-buttons">
                                            <button 
                                                className="action-btn view"
                                                title="View Product"
                                                onClick={() => navigate(`/marketplace/product/${product.id}`)}
                                            >
                                                <img src={Eye} alt="View" />
                                            </button>
                                            <button 
                                                className="action-btn edit"
                                                title="Edit Product"
                                                onClick={() => navigate(`/vendor/products/${product.id}/edit`)}
                                            >
                                                <img src={Edit} alt="Edit" />
                                            </button>
                                            <button 
                                                className="action-btn stock"
                                                title="Update Stock"
                                                onClick={() => setStockModal({ 
                                                    show: true, 
                                                    product, 
                                                    quantity: product.stock_quantity || 0 
                                                })}
                                            >
                                                📦
                                            </button>
                                            <button 
                                                className="action-btn delete"
                                                title="Delete Product"
                                                onClick={() => handleDeleteProduct(product.id)}
                                            >
                                                <img src={Trash} alt="Delete" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Stock Update Modal */}
            {stockModal.show && (
                <div className="modal-overlay" onClick={() => setStockModal({ show: false, product: null, quantity: 0 })}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Update Stock</h3>
                            <button 
                                className="modal-close"
                                onClick={() => setStockModal({ show: false, product: null, quantity: 0 })}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="product-name">{stockModal.product?.name}</p>
                            <div className="stock-input-group">
                                <label>Stock Quantity</label>
                                <div className="quantity-controls">
                                    <button 
                                        onClick={() => setStockModal(prev => ({ 
                                            ...prev, 
                                            quantity: Math.max(0, prev.quantity - 1) 
                                        }))}
                                    >
                                        -
                                    </button>
                                    <input
                                        type="number"
                                        min="0"
                                        value={stockModal.quantity}
                                        onChange={(e) => setStockModal(prev => ({ 
                                            ...prev, 
                                            quantity: parseInt(e.target.value) || 0 
                                        }))}
                                    />
                                    <button 
                                        onClick={() => setStockModal(prev => ({ 
                                            ...prev, 
                                            quantity: prev.quantity + 1 
                                        }))}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <Button 
                                text="Cancel" 
                                variant="outline"
                                onClick={() => setStockModal({ show: false, product: null, quantity: 0 })}
                            />
                            <Button 
                                text="Update Stock" 
                                variant="primary"
                                onClick={handleUpdateStock}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorProducts;